/**
 * Request Manager for ArbiWin API
 *
 * This module provides comprehensive request management including rate limiting,
 * request queuing, deduplication, cancellation, priority management, and
 * concurrent request limiting to prevent API abuse and optimize performance.
 *
 * Features:
 * - Rate limiting with sliding window algorithm
 * - Request queuing with priority management
 * - Request deduplication for identical requests
 * - Request cancellation utilities
 * - Concurrent request limiting
 * - Request retry with exponential backoff
 * - Performance monitoring and metrics
 * - Automatic cleanup and memory management
 *
 * Following constitution requirements for performance optimization
 * and API abuse prevention.
 */

/**
 * Request Priority Levels
 */
export const REQUEST_PRIORITY = {
  CRITICAL: 0,    // Authentication, subscription checks
  HIGH: 1,        // User actions, surebet data
  NORMAL: 2,      // General API calls
  LOW: 3,         // Background tasks, analytics
  BACKGROUND: 4   // Non-essential requests
};

/**
 * Request Status Constants
 */
export const REQUEST_STATUS = {
  PENDING: 'pending',
  QUEUED: 'queued',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  RATE_LIMITED: 'rate_limited',
  DEDUPLICATED: 'deduplicated'
};

/**
 * Rate Limiting Configuration
 */
export const RATE_LIMIT_CONFIG = {
  // Global rate limits
  GLOBAL: {
    maxRequests: 100,        // Max requests per window
    windowMs: 60000,         // 1 minute window
    burstLimit: 20           // Burst allowance
  },

  // Endpoint-specific rate limits
  ENDPOINTS: {
    '/api/core/markets-analysis': {
      maxRequests: 30,       // Surebet data is resource intensive
      windowMs: 60000,       // 1 minute window
      burstLimit: 5
    },
    '/api/auth/login': {
      maxRequests: 5,        // Prevent brute force
      windowMs: 300000,      // 5 minute window
      burstLimit: 2
    },
    '/api/auth/register': {
      maxRequests: 3,        // Prevent spam registration
      windowMs: 600000,      // 10 minute window
      burstLimit: 1
    },
    '/api/subscriptions/activate': {
      maxRequests: 10,       // Prevent subscription abuse
      windowMs: 300000,      // 5 minute window
      burstLimit: 2
    }
  },

  // User-based rate limits
  USER_LIMITS: {
    maxRequestsPerUser: 200,  // Per user per window
    windowMs: 60000,          // 1 minute window
    premiumMultiplier: 2      // Premium users get 2x limits
  }
};

/**
 * Request Management Configuration
 */
export const REQUEST_CONFIG = {
  MAX_CONCURRENT: 6,          // Maximum concurrent requests
  MAX_QUEUE_SIZE: 50,         // Maximum queued requests
  REQUEST_TIMEOUT: 30000,     // 30 seconds default timeout
  CLEANUP_INTERVAL: 300000,   // 5 minutes cleanup interval
  DEDUPLICATION_WINDOW: 5000, // 5 seconds deduplication window
  RETRY_DELAYS: [1000, 2000, 4000, 8000], // Exponential backoff delays
  MAX_RETRIES: 3
};

/**
 * Sliding Window Rate Limiter
 */
export class SlidingWindowRateLimiter {
  constructor(maxRequests, windowMs, burstLimit = 0) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.burstLimit = burstLimit;
    this.requests = new Map(); // key -> array of timestamps
    this.burstTokens = new Map(); // key -> remaining burst tokens

    // Start cleanup interval
    this.startCleanup();
  }

  /**
   * Check if request is allowed
   * @param {string} key - Rate limit key (user ID, IP, endpoint)
   * @returns {Object} Rate limit result
   */
  isAllowed(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;

    // Get existing requests for this key
    let keyRequests = this.requests.get(key) || [];

    // Remove requests outside the window
    keyRequests = keyRequests.filter(timestamp => timestamp > windowStart);

    // Update the requests array
    this.requests.set(key, keyRequests);

    // Check burst allowance first
    if (this.burstLimit > 0) {
      const burstTokens = this.burstTokens.get(key) || this.burstLimit;

      if (burstTokens > 0 && keyRequests.length < this.maxRequests) {
        this.burstTokens.set(key, burstTokens - 1);
        keyRequests.push(now);
        this.requests.set(key, keyRequests);

        return {
          allowed: true,
          remaining: this.maxRequests - keyRequests.length,
          resetTime: windowStart + this.windowMs,
          retryAfter: null,
          usedBurst: true
        };
      }
    }

    // Check regular rate limit
    if (keyRequests.length < this.maxRequests) {
      keyRequests.push(now);
      this.requests.set(key, keyRequests);

      return {
        allowed: true,
        remaining: this.maxRequests - keyRequests.length,
        resetTime: windowStart + this.windowMs,
        retryAfter: null,
        usedBurst: false
      };
    }

    // Rate limit exceeded
    const oldestRequest = Math.min(...keyRequests);
    const retryAfter = Math.max(0, oldestRequest + this.windowMs - now);

    return {
      allowed: false,
      remaining: 0,
      resetTime: oldestRequest + this.windowMs,
      retryAfter: retryAfter,
      usedBurst: false
    };
  }

  /**
   * Reset rate limits for a key
   * @param {string} key - Rate limit key
   */
  reset(key) {
    this.requests.delete(key);
    this.burstTokens.delete(key);
  }

  /**
   * Get current usage for a key
   * @param {string} key - Rate limit key
   * @returns {Object} Usage information
   */
  getUsage(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    const keyRequests = this.requests.get(key) || [];
    const validRequests = keyRequests.filter(timestamp => timestamp > windowStart);
    const burstTokens = this.burstTokens.get(key) || this.burstLimit;

    return {
      used: validRequests.length,
      remaining: this.maxRequests - validRequests.length,
      burstTokens: burstTokens,
      resetTime: windowStart + this.windowMs
    };
  }

  /**
   * Start cleanup interval to remove old data
   */
  startCleanup() {
    setInterval(() => {
      const now = Date.now();
      const cutoff = now - this.windowMs * 2; // Keep some extra history

      for (const [key, timestamps] of this.requests.entries()) {
        const validTimestamps = timestamps.filter(ts => ts > cutoff);
        if (validTimestamps.length === 0) {
          this.requests.delete(key);
          this.burstTokens.delete(key);
        } else {
          this.requests.set(key, validTimestamps);
        }
      }
    }, this.windowMs);
  }

  /**
   * Get statistics
   * @returns {Object} Rate limiter statistics
   */
  getStats() {
    return {
      totalKeys: this.requests.size,
      maxRequests: this.maxRequests,
      windowMs: this.windowMs,
      burstLimit: this.burstLimit,
      activeKeys: Array.from(this.requests.keys())
    };
  }
}

/**
 * Request Queue with Priority Management
 */
export class PriorityRequestQueue {
  constructor(maxSize = REQUEST_CONFIG.MAX_QUEUE_SIZE) {
    this.maxSize = maxSize;
    this.queues = new Map(); // priority -> array of requests
    this.requestMap = new Map(); // requestId -> request object
    this.totalSize = 0;

    // Initialize priority queues
    Object.values(REQUEST_PRIORITY).forEach(priority => {
      this.queues.set(priority, []);
    });
  }

  /**
   * Add request to queue
   * @param {Object} request - Request object
   * @returns {boolean} True if added successfully
   */
  enqueue(request) {
    if (this.totalSize >= this.maxSize) {
      // Remove lowest priority request to make space
      if (!this.removeLowPriorityRequest()) {
        return false; // Queue is full and can't remove anything
      }
    }

    const priority = request.priority || REQUEST_PRIORITY.NORMAL;
    const queue = this.queues.get(priority);

    queue.push(request);
    this.requestMap.set(request.id, request);
    this.totalSize++;

    request.status = REQUEST_STATUS.QUEUED;
    request.queuedAt = Date.now();

    return true;
  }

  /**
   * Get next request from queue (highest priority first)
   * @returns {Object|null} Next request or null if queue is empty
   */
  dequeue() {
    // Check queues in priority order
    for (const priority of Object.values(REQUEST_PRIORITY).sort((a, b) => a - b)) {
      const queue = this.queues.get(priority);
      if (queue.length > 0) {
        const request = queue.shift();
        this.requestMap.delete(request.id);
        this.totalSize--;

        request.status = REQUEST_STATUS.IN_PROGRESS;
        request.dequeuedAt = Date.now();

        return request;
      }
    }

    return null;
  }

  /**
   * Remove request from queue
   * @param {string} requestId - Request ID to remove
   * @returns {boolean} True if removed successfully
   */
  remove(requestId) {
    const request = this.requestMap.get(requestId);
    if (!request) {
      return false;
    }

    const priority = request.priority || REQUEST_PRIORITY.NORMAL;
    const queue = this.queues.get(priority);
    const index = queue.findIndex(r => r.id === requestId);

    if (index !== -1) {
      queue.splice(index, 1);
      this.requestMap.delete(requestId);
      this.totalSize--;

      request.status = REQUEST_STATUS.CANCELLED;
      request.cancelledAt = Date.now();

      return true;
    }

    return false;
  }

  /**
   * Remove lowest priority request to make space
   * @returns {boolean} True if removed successfully
   */
  removeLowPriorityRequest() {
    // Check queues in reverse priority order (lowest first)
    for (const priority of Object.values(REQUEST_PRIORITY).sort((a, b) => b - a)) {
      const queue = this.queues.get(priority);
      if (queue.length > 0) {
        const request = queue.shift();
        this.requestMap.delete(request.id);
        this.totalSize--;

        request.status = REQUEST_STATUS.CANCELLED;
        request.cancelledAt = Date.now();
        request.cancelReason = 'Queue overflow';

        return true;
      }
    }

    return false;
  }

  /**
   * Get queue size
   * @returns {number} Total number of queued requests
   */
  size() {
    return this.totalSize;
  }

  /**
   * Check if queue is empty
   * @returns {boolean} True if queue is empty
   */
  isEmpty() {
    return this.totalSize === 0;
  }

  /**
   * Get queue statistics
   * @returns {Object} Queue statistics
   */
  getStats() {
    const stats = {
      totalSize: this.totalSize,
      maxSize: this.maxSize,
      byPriority: {}
    };

    for (const [priority, queue] of this.queues.entries()) {
      const priorityName = Object.keys(REQUEST_PRIORITY).find(
        key => REQUEST_PRIORITY[key] === priority
      );
      stats.byPriority[priorityName] = queue.length;
    }

    return stats;
  }

  /**
   * Clear all queues
   */
  clear() {
    this.queues.forEach(queue => queue.length = 0);
    this.requestMap.clear();
    this.totalSize = 0;
  }
}

/**
 * Request Deduplication Manager
 */
export class RequestDeduplicator {
  constructor(windowMs = REQUEST_CONFIG.DEDUPLICATION_WINDOW) {
    this.windowMs = windowMs;
    this.requests = new Map(); // key -> { timestamp, promise, requestIds }

    // Start cleanup interval
    this.startCleanup();
  }

  /**
   * Generate deduplication key for request
   * @param {Object} config - Request configuration
   * @returns {string} Deduplication key
   */
  generateKey(config) {
    const { method, url, params, data } = config;
    const keyData = {
      method: method?.toLowerCase(),
      url,
      params: params ? JSON.stringify(params) : null,
      data: data ? JSON.stringify(data) : null
    };

    return JSON.stringify(keyData);
  }

  /**
   * Check if request should be deduplicated
   * @param {Object} config - Request configuration
   * @param {string} requestId - Request ID
   * @returns {Object} Deduplication result
   */
  checkDuplication(config, requestId) {
    const key = this.generateKey(config);
    const now = Date.now();
    const existing = this.requests.get(key);

    if (existing && (now - existing.timestamp) < this.windowMs) {
      // Add this request ID to the existing entry
      existing.requestIds.push(requestId);

      return {
        isDuplicate: true,
        promise: existing.promise,
        originalRequestIds: existing.requestIds.slice(0, -1), // All except current
        waitTime: this.windowMs - (now - existing.timestamp)
      };
    }

    // Not a duplicate, create new entry
    const entry = {
      timestamp: now,
      promise: null, // Will be set by caller
      requestIds: [requestId]
    };

    this.requests.set(key, entry);

    return {
      isDuplicate: false,
      key: key
    };
  }

  /**
   * Set promise for a deduplication key
   * @param {string} key - Deduplication key
   * @param {Promise} promise - Request promise
   */
  setPromise(key, promise) {
    const entry = this.requests.get(key);
    if (entry) {
      entry.promise = promise;
    }
  }

  /**
   * Remove deduplication entry
   * @param {string} key - Deduplication key
   */
  remove(key) {
    this.requests.delete(key);
  }

  /**
   * Start cleanup interval
   */
  startCleanup() {
    setInterval(() => {
      const now = Date.now();
      const cutoff = now - this.windowMs * 2; // Keep some extra history

      for (const [key, entry] of this.requests.entries()) {
        if (entry.timestamp < cutoff) {
          this.requests.delete(key);
        }
      }
    }, this.windowMs);
  }

  /**
   * Get statistics
   * @returns {Object} Deduplication statistics
   */
  getStats() {
    return {
      activeKeys: this.requests.size,
      windowMs: this.windowMs,
      entries: Array.from(this.requests.entries()).map(([key, entry]) => ({
        key,
        age: Date.now() - entry.timestamp,
        requestCount: entry.requestIds.length
      }))
    };
  }
}

/**
 * Request Cancellation Manager
 */
export class RequestCancellationManager {
  constructor() {
    this.cancelTokens = new Map(); // requestId -> cancel function
    this.abortControllers = new Map(); // requestId -> AbortController
  }

  /**
   * Create cancellation token for request
   * @param {string} requestId - Request ID
   * @returns {Object} Cancellation utilities
   */
  createCancellationToken(requestId) {
    // Create AbortController for modern cancellation
    const abortController = new AbortController();
    this.abortControllers.set(requestId, abortController);

    // Create cancel function
    const cancelFunction = (reason = 'Request cancelled') => {
      abortController.abort(reason);
      this.cleanup(requestId);
    };

    this.cancelTokens.set(requestId, cancelFunction);

    return {
      signal: abortController.signal,
      cancel: cancelFunction,
      isCancelled: () => abortController.signal.aborted
    };
  }

  /**
   * Cancel request by ID
   * @param {string} requestId - Request ID to cancel
   * @param {string} reason - Cancellation reason
   * @returns {boolean} True if cancelled successfully
   */
  cancelRequest(requestId, reason = 'Request cancelled') {
    const cancelFunction = this.cancelTokens.get(requestId);
    if (cancelFunction) {
      cancelFunction(reason);
      return true;
    }
    return false;
  }

  /**
   * Cancel multiple requests
   * @param {Array<string>} requestIds - Request IDs to cancel
   * @param {string} reason - Cancellation reason
   * @returns {number} Number of requests cancelled
   */
  cancelRequests(requestIds, reason = 'Batch cancellation') {
    let cancelled = 0;
    requestIds.forEach(requestId => {
      if (this.cancelRequest(requestId, reason)) {
        cancelled++;
      }
    });
    return cancelled;
  }

  /**
   * Cancel all pending requests
   * @param {string} reason - Cancellation reason
   * @returns {number} Number of requests cancelled
   */
  cancelAll(reason = 'Cancel all requests') {
    const requestIds = Array.from(this.cancelTokens.keys());
    return this.cancelRequests(requestIds, reason);
  }

  /**
   * Cleanup cancellation resources
   * @param {string} requestId - Request ID
   */
  cleanup(requestId) {
    this.cancelTokens.delete(requestId);
    this.abortControllers.delete(requestId);
  }

  /**
   * Get active cancellation tokens
   * @returns {Array<string>} Active request IDs
   */
  getActiveRequests() {
    return Array.from(this.cancelTokens.keys());
  }

  /**
   * Get statistics
   * @returns {Object} Cancellation statistics
   */
  getStats() {
    return {
      activeTokens: this.cancelTokens.size,
      activeControllers: this.abortControllers.size
    };
  }
}

/**
 * Request Performance Monitor
 */
export class RequestPerformanceMonitor {
  constructor() {
    this.metrics = {
      totalRequests: 0,
      completedRequests: 0,
      failedRequests: 0,
      cancelledRequests: 0,
      rateLimitedRequests: 0,
      deduplicatedRequests: 0,
      averageResponseTime: 0,
      totalResponseTime: 0,
      concurrentRequests: 0,
      maxConcurrentRequests: 0,
      queuedRequests: 0,
      maxQueuedRequests: 0
    };

    this.requestTimes = new Map(); // requestId -> start time
    this.concurrentTracker = new Set(); // active request IDs
  }

  /**
   * Start tracking request
   * @param {string} requestId - Request ID
   */
  startRequest(requestId) {
    this.metrics.totalRequests++;
    this.requestTimes.set(requestId, performance.now());
    this.concurrentTracker.add(requestId);

    this.metrics.concurrentRequests = this.concurrentTracker.size;
    this.metrics.maxConcurrentRequests = Math.max(
      this.metrics.maxConcurrentRequests,
      this.metrics.concurrentRequests
    );
  }

  /**
   * End tracking request
   * @param {string} requestId - Request ID
   * @param {string} status - Request status
   */
  endRequest(requestId, status = REQUEST_STATUS.COMPLETED) {
    const startTime = this.requestTimes.get(requestId);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.metrics.totalResponseTime += duration;
      this.requestTimes.delete(requestId);
    }

    this.concurrentTracker.delete(requestId);
    this.metrics.concurrentRequests = this.concurrentTracker.size;

    // Update status-specific metrics
    switch (status) {
      case REQUEST_STATUS.COMPLETED:
        this.metrics.completedRequests++;
        break;
      case REQUEST_STATUS.FAILED:
        this.metrics.failedRequests++;
        break;
      case REQUEST_STATUS.CANCELLED:
        this.metrics.cancelledRequests++;
        break;
      case REQUEST_STATUS.RATE_LIMITED:
        this.metrics.rateLimitedRequests++;
        break;
      case REQUEST_STATUS.DEDUPLICATED:
        this.metrics.deduplicatedRequests++;
        break;
    }

    // Update average response time
    const completedRequests = this.metrics.completedRequests + this.metrics.failedRequests;
    if (completedRequests > 0) {
      this.metrics.averageResponseTime = this.metrics.totalResponseTime / completedRequests;
    }
  }

  /**
   * Update queue metrics
   * @param {number} queueSize - Current queue size
   */
  updateQueueMetrics(queueSize) {
    this.metrics.queuedRequests = queueSize;
    this.metrics.maxQueuedRequests = Math.max(
      this.metrics.maxQueuedRequests,
      queueSize
    );
  }

  /**
   * Get performance metrics
   * @returns {Object} Performance metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      successRate: this.metrics.totalRequests > 0
        ? this.metrics.completedRequests / this.metrics.totalRequests
        : 0,
      failureRate: this.metrics.totalRequests > 0
        ? this.metrics.failedRequests / this.metrics.totalRequests
        : 0,
      cancellationRate: this.metrics.totalRequests > 0
        ? this.metrics.cancelledRequests / this.metrics.totalRequests
        : 0,
      rateLimitRate: this.metrics.totalRequests > 0
        ? this.metrics.rateLimitedRequests / this.metrics.totalRequests
        : 0,
      deduplicationRate: this.metrics.totalRequests > 0
        ? this.metrics.deduplicatedRequests / this.metrics.totalRequests
        : 0
    };
  }

  /**
   * Reset metrics
   */
  reset() {
    this.metrics = {
      totalRequests: 0,
      completedRequests: 0,
      failedRequests: 0,
      cancelledRequests: 0,
      rateLimitedRequests: 0,
      deduplicatedRequests: 0,
      averageResponseTime: 0,
      totalResponseTime: 0,
      concurrentRequests: 0,
      maxConcurrentRequests: 0,
      queuedRequests: 0,
      maxQueuedRequests: 0
    };

    this.requestTimes.clear();
    this.concurrentTracker.clear();
  }
}

/**
 * Main Request Manager
 */
export class RequestManager {
  constructor(options = {}) {
    // Configuration
    this.maxConcurrent = options.maxConcurrent || REQUEST_CONFIG.MAX_CONCURRENT;
    this.maxQueueSize = options.maxQueueSize || REQUEST_CONFIG.MAX_QUEUE_SIZE;
    this.requestTimeout = options.requestTimeout || REQUEST_CONFIG.REQUEST_TIMEOUT;
    this.enableRateLimiting = options.enableRateLimiting !== false;
    this.enableDeduplication = options.enableDeduplication !== false;
    this.enablePriorityQueue = options.enablePriorityQueue !== false;

    // Core components
    this.queue = new PriorityRequestQueue(this.maxQueueSize);
    this.cancellationManager = new RequestCancellationManager();
    this.performanceMonitor = new RequestPerformanceMonitor();
    this.deduplicator = new RequestDeduplicator();

    // Rate limiters
    this.globalRateLimiter = new SlidingWindowRateLimiter(
      RATE_LIMIT_CONFIG.GLOBAL.maxRequests,
      RATE_LIMIT_CONFIG.GLOBAL.windowMs,
      RATE_LIMIT_CONFIG.GLOBAL.burstLimit
    );

    this.endpointRateLimiters = new Map();
    this.userRateLimiters = new Map();

    // Request tracking
    this.activeRequests = new Map(); // requestId -> request object
    this.requestCounter = 0;

    // Start processing loop
    this.startProcessingLoop();

    // Start cleanup interval
    this.startCleanup();
  }

  /**
   * Execute request with full management
   * @param {Function} requestFn - Function that returns a promise
   * @param {Object} config - Request configuration
   * @returns {Promise} Request promise
   */
  async executeRequest(requestFn, config = {}) {
    const requestId = this.generateRequestId();
    const priority = config.priority || REQUEST_PRIORITY.NORMAL;
    const timeout = config.timeout || this.requestTimeout;
    const bypassRateLimit = config.bypassRateLimit || false;
    const bypassDeduplication = config.bypassDeduplication || false;
    const userId = config.userId;
    const endpoint = config.url;

    // Create request object
    const request = {
      id: requestId,
      requestFn,
      config,
      priority,
      timeout,
      userId,
      endpoint,
      status: REQUEST_STATUS.PENDING,
      createdAt: Date.now(),
      attempts: 0,
      maxRetries: config.maxRetries || REQUEST_CONFIG.MAX_RETRIES
    };

    try {
      // Check deduplication first
      if (this.enableDeduplication && !bypassDeduplication) {
        const deduplicationResult = this.deduplicator.checkDuplication(config, requestId);

        if (deduplicationResult.isDuplicate) {
          this.performanceMonitor.endRequest(requestId, REQUEST_STATUS.DEDUPLICATED);
          return deduplicationResult.promise;
        }

        request.deduplicationKey = deduplicationResult.key;
      }

      // Check rate limits
      if (this.enableRateLimiting && !bypassRateLimit) {
        const rateLimitResult = this.checkRateLimits(request);
        if (!rateLimitResult.allowed) {
          request.status = REQUEST_STATUS.RATE_LIMITED;
          request.retryAfter = rateLimitResult.retryAfter;

          this.performanceMonitor.endRequest(requestId, REQUEST_STATUS.RATE_LIMITED);

          const error = new Error(`Rate limit exceeded. Retry after ${rateLimitResult.retryAfter}ms`);
          error.code = 'RATE_LIMITED';
          error.retryAfter = rateLimitResult.retryAfter;
          throw error;
        }
      }

      // Create request promise
      const requestPromise = this.processRequest(request);

      // Set deduplication promise if applicable
      if (request.deduplicationKey) {
        this.deduplicator.setPromise(request.deduplicationKey, requestPromise);
      }

      return await requestPromise;

    } catch (error) {
      // Clean up deduplication if needed
      if (request.deduplicationKey) {
        this.deduplicator.remove(request.deduplicationKey);
      }

      throw error;
    }
  }

  /**
   * Process individual request
   * @param {Object} request - Request object
   * @returns {Promise} Request result
   */
  async processRequest(request) {
    return new Promise((resolve, reject) => {
      request.resolve = resolve;
      request.reject = reject;

      // Check if we can execute immediately
      if (this.activeRequests.size < this.maxConcurrent) {
        this.executeImmediately(request);
      } else {
        // Add to queue
        if (this.enablePriorityQueue) {
          if (this.queue.enqueue(request)) {
            this.performanceMonitor.updateQueueMetrics(this.queue.size());
          } else {
            // Queue is full
            const error = new Error('Request queue is full');
            error.code = 'QUEUE_FULL';
            request.reject(error);
            return;
          }
        } else {
          // Execute immediately without queue management
          this.executeImmediately(request);
        }
      }
    });
  }

  /**
   * Execute request immediately
   * @param {Object} request - Request object
   */
  async executeImmediately(request) {
    const { id: requestId, requestFn, timeout } = request;

    // Start performance tracking
    this.performanceMonitor.startRequest(requestId);

    // Add to active requests
    this.activeRequests.set(requestId, request);
    request.status = REQUEST_STATUS.IN_PROGRESS;
    request.startedAt = Date.now();

    // Create cancellation token
    const cancellationToken = this.cancellationManager.createCancellationToken(requestId);
    request.cancellationToken = cancellationToken;

    try {
      // Execute request with timeout and cancellation
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          const error = new Error('Request timeout');
          error.code = 'TIMEOUT';
          reject(error);
        }, timeout);
      });

      const requestPromise = requestFn({
        signal: cancellationToken.signal,
        requestId: requestId
      });

      const result = await Promise.race([requestPromise, timeoutPromise]);

      // Request completed successfully
      request.status = REQUEST_STATUS.COMPLETED;
      request.completedAt = Date.now();
      request.result = result;

      this.performanceMonitor.endRequest(requestId, REQUEST_STATUS.COMPLETED);
      this.cleanup(requestId);

      request.resolve(result);

    } catch (error) {
      // Handle request failure
      request.status = REQUEST_STATUS.FAILED;
      request.failedAt = Date.now();
      request.error = error;

      // Check if we should retry
      if (this.shouldRetry(request, error)) {
        this.scheduleRetry(request);
      } else {
        this.performanceMonitor.endRequest(requestId, REQUEST_STATUS.FAILED);
        this.cleanup(requestId);
        request.reject(error);
      }
    }
  }

  /**
   * Check if request should be retried
   * @param {Object} request - Request object
   * @param {Error} error - Error that occurred
   * @returns {boolean} True if should retry
   */
  shouldRetry(request, error) {
    // Don't retry if we've exceeded max retries
    if (request.attempts >= request.maxRetries) {
      return false;
    }

    // Don't retry certain error types
    const nonRetryableCodes = ['CANCELLED', 'RATE_LIMITED', 'QUEUE_FULL', 'VALIDATION_ERROR'];
    if (nonRetryableCodes.includes(error.code)) {
      return false;
    }

    // Don't retry 4xx errors (except 429 rate limit)
    if (error.response && error.response.status >= 400 && error.response.status < 500) {
      return error.response.status === 429; // Retry rate limit errors
    }

    return true;
  }

  /**
   * Schedule request retry
   * @param {Object} request - Request object
   */
  scheduleRetry(request) {
    request.attempts++;
    request.status = REQUEST_STATUS.PENDING;

    const delayIndex = Math.min(request.attempts - 1, REQUEST_CONFIG.RETRY_DELAYS.length - 1);
    const delay = REQUEST_CONFIG.RETRY_DELAYS[delayIndex];

    setTimeout(() => {
      // Add back to queue or execute immediately
      if (this.activeRequests.size < this.maxConcurrent) {
        this.executeImmediately(request);
      } else if (this.enablePriorityQueue) {
        this.queue.enqueue(request);
        this.performanceMonitor.updateQueueMetrics(this.queue.size());
      }
    }, delay);
  }

  /**
   * Check rate limits for request
   * @param {Object} request - Request object
   * @returns {Object} Rate limit result
   */
  checkRateLimits(request) {
    const { userId, endpoint } = request;

    // Check global rate limit
    const globalResult = this.globalRateLimiter.isAllowed('global');
    if (!globalResult.allowed) {
      return globalResult;
    }

    // Check endpoint-specific rate limit
    if (endpoint && RATE_LIMIT_CONFIG.ENDPOINTS[endpoint]) {
      const endpointConfig = RATE_LIMIT_CONFIG.ENDPOINTS[endpoint];

      if (!this.endpointRateLimiters.has(endpoint)) {
        this.endpointRateLimiters.set(endpoint, new SlidingWindowRateLimiter(
          endpointConfig.maxRequests,
          endpointConfig.windowMs,
          endpointConfig.burstLimit
        ));
      }

      const endpointLimiter = this.endpointRateLimiters.get(endpoint);
      const endpointResult = endpointLimiter.isAllowed(endpoint);
      if (!endpointResult.allowed) {
        return endpointResult;
      }
    }

    // Check user-specific rate limit
    if (userId) {
      if (!this.userRateLimiters.has(userId)) {
        const userConfig = RATE_LIMIT_CONFIG.USER_LIMITS;
        this.userRateLimiters.set(userId, new SlidingWindowRateLimiter(
          userConfig.maxRequestsPerUser,
          userConfig.windowMs
        ));
      }

      const userLimiter = this.userRateLimiters.get(userId);
      const userResult = userLimiter.isAllowed(userId);
      if (!userResult.allowed) {
        return userResult;
      }
    }

    return { allowed: true };
  }

  /**
   * Start request processing loop
   */
  startProcessingLoop() {
    setInterval(() => {
      // Process queued requests if we have capacity
      while (this.activeRequests.size < this.maxConcurrent && !this.queue.isEmpty()) {
        const request = this.queue.dequeue();
        if (request) {
          this.executeImmediately(request);
          this.performanceMonitor.updateQueueMetrics(this.queue.size());
        }
      }
    }, 100); // Check every 100ms
  }

  /**
   * Start cleanup interval
   */
  startCleanup() {
    setInterval(() => {
      const now = Date.now();
      const cleanupAge = REQUEST_CONFIG.CLEANUP_INTERVAL;

      // Clean up old completed requests
      for (const [requestId, request] of this.activeRequests.entries()) {
        if (request.completedAt && (now - request.completedAt) > cleanupAge) {
          this.cleanup(requestId);
        }
      }
    }, REQUEST_CONFIG.CLEANUP_INTERVAL);
  }

  /**
   * Generate unique request ID
   * @returns {string} Request ID
   */
  generateRequestId() {
    return `req_${Date.now()}_${++this.requestCounter}`;
  }

  /**
   * Cleanup request resources
   * @param {string} requestId - Request ID
   */
  cleanup(requestId) {
    const request = this.activeRequests.get(requestId);
    if (request) {
      // Clean up cancellation token
      this.cancellationManager.cleanup(requestId);

      // Clean up deduplication if needed
      if (request.deduplicationKey) {
        this.deduplicator.remove(request.deduplicationKey);
      }

      // Remove from active requests
      this.activeRequests.delete(requestId);
    }
  }

  /**
   * Cancel request by ID
   * @param {string} requestId - Request ID
   * @param {string} reason - Cancellation reason
   * @returns {boolean} True if cancelled
   */
  cancelRequest(requestId, reason = 'Request cancelled') {
    // Try to cancel from queue first
    if (this.queue.remove(requestId)) {
      this.performanceMonitor.updateQueueMetrics(this.queue.size());
      return true;
    }

    // Cancel active request
    const cancelled = this.cancellationManager.cancelRequest(requestId, reason);
    if (cancelled) {
      const request = this.activeRequests.get(requestId);
      if (request) {
        request.status = REQUEST_STATUS.CANCELLED;
        request.cancelledAt = Date.now();
        this.performanceMonitor.endRequest(requestId, REQUEST_STATUS.CANCELLED);

        const error = new Error(reason);
        error.code = 'CANCELLED';
        request.reject(error);

        this.cleanup(requestId);
      }
    }

    return cancelled;
  }

  /**
   * Cancel all requests
   * @param {string} reason - Cancellation reason
   * @returns {number} Number of cancelled requests
   */
  cancelAllRequests(reason = 'Cancel all requests') {
    let cancelled = 0;

    // Cancel queued requests
    const queuedIds = Array.from(this.queue.requestMap.keys());
    queuedIds.forEach(requestId => {
      if (this.queue.remove(requestId)) {
        cancelled++;
      }
    });

    // Cancel active requests
    const activeIds = Array.from(this.activeRequests.keys());
    cancelled += this.cancellationManager.cancelRequests(activeIds, reason);

    this.performanceMonitor.updateQueueMetrics(this.queue.size());

    return cancelled;
  }

  /**
   * Get comprehensive statistics
   * @returns {Object} Request manager statistics
   */
  getStats() {
    return {
      performance: this.performanceMonitor.getMetrics(),
      queue: this.queue.getStats(),
      cancellation: this.cancellationManager.getStats(),
      deduplication: this.deduplicator.getStats(),
      rateLimiting: {
        global: this.globalRateLimiter.getStats(),
        endpoints: Object.fromEntries(
          Array.from(this.endpointRateLimiters.entries()).map(([endpoint, limiter]) => [
            endpoint,
            limiter.getStats()
          ])
        ),
        users: this.userRateLimiters.size
      },
      activeRequests: this.activeRequests.size,
      configuration: {
        maxConcurrent: this.maxConcurrent,
        maxQueueSize: this.maxQueueSize,
        requestTimeout: this.requestTimeout,
        enableRateLimiting: this.enableRateLimiting,
        enableDeduplication: this.enableDeduplication,
        enablePriorityQueue: this.enablePriorityQueue
      }
    };
  }

  /**
   * Reset all statistics
   */
  resetStats() {
    this.performanceMonitor.reset();
    this.queue.clear();
    this.deduplicator = new RequestDeduplicator();
    this.globalRateLimiter = new SlidingWindowRateLimiter(
      RATE_LIMIT_CONFIG.GLOBAL.maxRequests,
      RATE_LIMIT_CONFIG.GLOBAL.windowMs,
      RATE_LIMIT_CONFIG.GLOBAL.burstLimit
    );
    this.endpointRateLimiters.clear();
    this.userRateLimiters.clear();
  }
}

/**
 * Create global request manager instance
 */
export const requestManager = new RequestManager({
  maxConcurrent: REQUEST_CONFIG.MAX_CONCURRENT,
  maxQueueSize: REQUEST_CONFIG.MAX_QUEUE_SIZE,
  requestTimeout: REQUEST_CONFIG.REQUEST_TIMEOUT,
  enableRateLimiting: true,
  enableDeduplication: true,
  enablePriorityQueue: true
});

/**
 * Convenience functions for request management
 */
export const executeRequest = (requestFn, config = {}) => {
  return requestManager.executeRequest(requestFn, config);
};

export const cancelRequest = (requestId, reason) => {
  return requestManager.cancelRequest(requestId, reason);
};

export const cancelAllRequests = (reason) => {
  return requestManager.cancelAllRequests(reason);
};

export const getRequestStats = () => {
  return requestManager.getStats();
};

/**
 * Export request management utilities
 */
export const requestUtils = {
  execute: executeRequest,
  cancel: cancelRequest,
  cancelAll: cancelAllRequests,
  getStats: getRequestStats,
  resetStats: () => requestManager.resetStats(),
  createManager: (options) => new RequestManager(options)
};

export default {
  RequestManager,
  SlidingWindowRateLimiter,
  PriorityRequestQueue,
  RequestDeduplicator,
  RequestCancellationManager,
  RequestPerformanceMonitor,
  requestManager,
  requestUtils,
  executeRequest,
  cancelRequest,
  cancelAllRequests,
  getRequestStats,
  REQUEST_PRIORITY,
  REQUEST_STATUS,
  RATE_LIMIT_CONFIG,
  REQUEST_CONFIG
};
